import React from "react";
import {
  Button,
  Divider,
  Grid,
  NumberController,
  PanelModal,
  Text,
} from "@pushpress/shared-components";
import { AppointmentCredit } from "../PlanDrawer.types";
import { useForm } from "react-hook-form";
import AppointmentTypeSelect from "../../../../components/controllers/AppointmentTypeSelect";
import { AppointmentType } from "../../../../apis/appointmentApi";

interface AppointmentAccessModalProps {
  visible: boolean;
  appointmentCredit?: AppointmentCredit;
  selectedAppointmentCredits?: AppointmentCredit[];
  onClose: VoidFunction;
  onDelete: VoidFunction;
  onConfirm: (credit: AppointmentCredit) => void;
}

const AppointmentAccessModal = (props: AppointmentAccessModalProps) => {
  const { visible, onClose } = props;

  return (
    <PanelModal
      visible={visible}
      closeModal={onClose}
      width="400px"
      disableClickAway
      disableEscapeKeyDown
    >
      <AppointmentAccessContent {...props} />
    </PanelModal>
  );
};

const AppointmentAccessContent = (props: AppointmentAccessModalProps) => {
  const { appointmentCredit, selectedAppointmentCredits, onConfirm, onDelete } =
    props;

  const { control, handleSubmit } = useForm({
    defaultValues: appointmentCredit,
    mode: "onTouched",
  });

  /**
   * Hide the appointment type options that are already selected (selectedAppointmentCredits). The same appointment type can't be added twice.
   */
  const filterAppointmentTypeOption = (option: {
    data: AppointmentType;
  }): boolean => {
    // if there is no selection show the option
    if (!selectedAppointmentCredits) {
      return true;
    }
    // if editing, return true if the option is the same as the current appointment type
    if (option.data.uuid === appointmentCredit?.appointmentType.uuid) {
      return true;
    }
    // don't show the option if it's already selected
    return !selectedAppointmentCredits.find(
      (appointment) => appointment.appointmentType.uuid === option.data.uuid,
    );
  };

  const isEditing = !!appointmentCredit;

  return (
    <>
      <PanelModal.Header
        title="Appointment access"
        titleAlign="center"
        showCloseButton
      />
      <PanelModal.Body>
        <form noValidate onSubmit={handleSubmit(onConfirm)}>
          <AppointmentTypeSelect
            control={control}
            name="appointmentType"
            label="Appointment type"
            hideLabel
            required
            filterOption={filterAppointmentTypeOption}
          />
          <NumberController
            control={control}
            name="quantity"
            label="Credits per billing period"
            required
          />
          <Grid container>
            <Grid item xs={10}>
              <NumberController
                control={control}
                name="expirationDays"
                label="Credits expire after"
                required
              />
            </Grid>
            <Grid item xs={2}>
              <Text variant="body-lg" color="wolf-800" className="mt-4">
                days
              </Text>
            </Grid>
          </Grid>
          <Divider />
          <Text
            variant="body-lg"
            color="wolf-800"
            weight="600"
            className="mt-4"
          >
            Payroll reporting (optional)
          </Text>
          <Text
            variant="body-md"
            color="wolf-600"
            weight="400"
            className="mt-1"
          >
            Set a value for appointments on this plan (recommended if you pay
            your staff a % rate). This WILL NOT affect what a client pays for
            this plan.
          </Text>
          <NumberController
            control={control}
            name="value"
            label="Payroll value per credit"
            className="mt-3"
            startText="$"
          />
          <Divider marginRight={-24} marginLeft={-24} className="mb-4" />
          {isEditing ? (
            <Grid container justifyContent="space-between" className="pl-2">
              <Button
                text="Delete"
                buttonType="text"
                type="button"
                style={{ color: "#F5222D" }}
                onClick={onDelete}
              />
              <Button text="Save" buttonType="primary" type="submit" />
            </Grid>
          ) : (
            <Button text="Save" buttonType="primary" fullWidth type="submit" />
          )}
        </form>
      </PanelModal.Body>
    </>
  );
};

export default AppointmentAccessModal;
